import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { LogIn, Truck, Loader2 } from 'lucide-react';

// All roles go to home, which handles module-based redirects
const DEFAULT_REDIRECT = '/home';

export default function LoginPage() {
    const navigate = useNavigate();
    const { login, isAuthenticated, user } = useAuth();
    const [loading, setLoading] = useState(false);
    
    // Login form
    const [loginEmail, setLoginEmail] = useState('');
    const [loginPassword, setLoginPassword] = useState('');

    useEffect(() => {
        if (isAuthenticated && user) {
            // Si usuario solo tiene acceso a un módulo, redirigir directamente
            if (user.modulos_acceso?.length === 1) {
                const singleModuleRoutes = {
                    alarmas_ls: '/alarmas/panel',
                    gestion_4sat: '/gestion4sat/dashboard',
                    guias_despacho: '/guias/dashboard',
                    bomberos: '/bomberos/dashboard',
                    ordenes_servicio: '/ordenes/dashboard',
                    diesel: '/diesel/dashboard',
                    integrador_codelco: '/codelco/dashboard',
                };
                const route = singleModuleRoutes[user.modulos_acceso[0]];
                if (route) {
                    navigate(route);
                    return;
                }
            }
            navigate(DEFAULT_REDIRECT);
        }
    }, [isAuthenticated, user, navigate]);

    const handleLogin = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const userData = await login(loginEmail, loginPassword);
            toast.success(`Bienvenido, ${userData.nombre}`);
            // useEffect handles redirect based on modulos_acceso
        } catch (err) {
            toast.error(err.message || 'Error al iniciar sesión');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-2">
            {/* Left side - Hero Image */}
            <div 
                className="hidden lg:flex flex-col justify-end p-8 bg-cover bg-center relative"
                style={{
                    backgroundImage: 'url(https://images.unsplash.com/photo-1766788467067-d443f19314b6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBpbmR1c3RyaWFsJTIwZmFjdG9yeSUyMGV4dGVyaW9yJTIwc3Vuc2V0fGVufDB8fHx8MTc3MTkwNDczMnww&ixlib=rb-4.1.0&q=85)',
                }}
            >
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
                <div className="relative z-10 text-white">
                    <div className="flex items-center gap-3 mb-4">
                        <Truck className="h-10 w-10 text-primary" />
                        <h1 className="font-heading font-black text-4xl uppercase tracking-tight">
                            Multisistemas
                        </h1>
                    </div>
                    <p className="text-xl font-medium text-white/90 max-w-md">
                        Desarrollo de Software a Medida
                    </p>
                    <p className="text-white/70 mt-2 max-w-md">
                        Soluciones tecnológicas diseñadas según sus requerimientos específicos. Calidad, cumplimiento y precios competitivos.
                    </p>
                </div>
            </div>

            {/* Right side - Auth Forms */}
            <div className="flex items-center justify-center p-4 md:p-8 bg-background grid-background">
                <div className="w-full max-w-md space-y-6">
                    {/* Mobile Logo */}
                    <div className="lg:hidden text-center mb-8">
                        <div className="flex items-center justify-center gap-3 mb-2">
                            <Truck className="h-8 w-8 text-primary" />
                            <h1 className="font-heading font-black text-3xl uppercase tracking-tight text-primary">
                                Multisistemas
                            </h1>
                        </div>
                        <p className="text-muted-foreground text-sm">
                            Desarrollo de Sistemas a Medida
                        </p>
                    </div>

                    <Card className="border-2 shadow-[4px_4px_0px_0px_rgba(0,0,0,0.1)]">
                        <CardHeader className="space-y-1">
                            <CardTitle className="font-heading text-2xl font-bold uppercase tracking-tight">
                                Acceso al Sistema
                            </CardTitle>
                            <CardDescription>
                                Ingresa tus credenciales para acceder
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <form onSubmit={handleLogin} className="space-y-4">
                                <div className="space-y-2">
                                    <Label htmlFor="login-email" className="input-label">
                                        Correo Electrónico
                                    </Label>
                                    <Input
                                        id="login-email"
                                        type="email"
                                        value={loginEmail}
                                        onChange={(e) => setLoginEmail(e.target.value)}
                                        placeholder="correo@empresa.com"
                                                className="input-industrial"
                                                required
                                                data-testid="login-email"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="login-password" className="input-label">
                                                Contraseña
                                            </Label>
                                            <Input
                                                id="login-password"
                                                type="password"
                                                value={loginPassword}
                                                onChange={(e) => setLoginPassword(e.target.value)}
                                                placeholder="••••••••"
                                                className="input-industrial"
                                                required
                                                data-testid="login-password"
                                            />
                                        </div>
                                        <Button
                                            type="submit"
                                            className="w-full btn-industrial bg-primary text-primary-foreground"
                                            disabled={loading}
                                            data-testid="login-submit"
                                        >
                                            {loading ? (
                                                <Loader2 className="h-5 w-5 animate-spin" />
                                            ) : (
                                                <>
                                                    <LogIn className="h-5 w-5 mr-2" />
                                                    Ingresar
                                                </>
                                            )}
                                        </Button>
                                    </form>
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
}
